import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Truck } from "lucide-react";
import { API, COMPANY, LOGO_URL } from "@/lib/api";

function getSessionId() {
  let sid = localStorage.getItem("laronta_chat_session");
  if (!sid) {
    sid = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem("laronta_chat_session", sid);
  }
  return sid;
}

const WELCOME = {
  role: "assistant",
  content: "Hi! I'm the Laronta Assistant. Ask me about our services, service area, careers — or give me a tracking number and I'll check your shipment.",
};

function Bubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`chat-message-${m.role}`}
        className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser ? "bg-primary-brand text-white" : "bg-slate-100 text-slate-900"
        }`}
      >
        {m.content || "…"}
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);
  const sidRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    sidRef.current = getSessionId();
    (async () => {
      try {
        const res = await fetch(`${API}/chat/history/${sidRef.current}`);
        const hist = res.ok ? await res.json() : [];
        setMessages(hist.length ? hist : [WELCOME]);
      } catch (error) {
        console.error("Chat history load failed:", error);
        setMessages([WELCOME]);
      } finally {
        setLoaded(true);
      }
    })();
  }, [open, loaded]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((ms) => [...ms, { role: "user", content: text }, { role: "assistant", content: "" }]);

    const appendDelta = (delta) =>
      setMessages((ms) => {
        const next = [...ms];
        next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + delta };
        return next;
      });

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop();
        for (const chunk of chunks) {
          const line = chunk.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          const obj = JSON.parse(data);
          if (obj.delta) appendDelta(obj.delta);
          if (obj.error) appendDelta(obj.error);
        }
      }
    } catch (error) {
      console.error("Chat send failed:", error);
      appendDelta(`Sorry, I couldn't respond right now. Please call ${COMPANY.phone}.`);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] max-h-[70vh] bg-white border border-slate-200 shadow-2xl flex flex-col"
        >
          <div className="flex items-center justify-between bg-primary-brand text-white px-4 py-3">
            <div className="flex items-center gap-2.5">
              <img src={LOGO_URL} alt="Laronta Pro" className="h-8 w-8 rounded-full object-cover ring-2 ring-white/40" />
              <div>
                <div className="font-heading font-black text-sm leading-tight">Laronta Assistant</div>
                <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">AI Support · Online</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn" className="p-1 hover:bg-white/15 rounded">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-brand-bg">
            {!loaded && <div className="text-center text-xs text-slate-400 py-6">Loading…</div>}
            {messages.map((m, i) => <Bubble key={i} m={m} />)}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="flex items-center gap-2 border-t border-slate-200 p-3 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about services, tracking…"
              data-testid="chat-input"
              className="flex-1 bg-transparent border border-slate-200 focus:border-primary-brand outline-none px-3 py-2.5 text-sm text-slate-900"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="chat-send-btn"
              className="bg-primary-brand hover:bg-[#E04F00] disabled:opacity-50 text-white p-2.5"
            >
              {streaming ? <Truck className="h-4 w-4 animate-pulse" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-widget-button"
        aria-label="Chat with Laronta Assistant"
        className="fixed bottom-5 right-4 sm:right-6 z-50 h-14 w-14 rounded-full bg-primary-brand hover:bg-[#E04F00] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </>
  );
}
